import { useRouter } from "next/router";
import { useContext, useState } from "react";
import Head from "next/head";
import { GlobalContext } from "../../stores/use_global_state";
import Layout from "../../components/layout";
import getCommonLocales from "../../locales/locales_common";
import getErrorLocales from "../../locales/locales_error";
import getRemoveBgPageLocales from "../../locales/locales_remove_bg";
import style from "./photo-to-sketch.module.scss";
import { fileExtensionList } from "../../constants/file_extension";
import { addToLocalStorage } from "../../utils/nonAuthLocalStorage";

import * as ga from "../../lib/ga";
import AuthModal from "../../components/AuthModal/AuthModal";
import LandingLayoutTypeA from "../../components/avatar-generator/LandingLayout/LayoutTypeA";

export default function PhotoToSketch() {
	const router = useRouter();
	const { state, dispatch } = useContext(GlobalContext);
	const [uploadFileLoading, setUploadFileLoading] = useState(false);
	const [showAuthModal, setShowAuthModal] = useState(false);

	const checkNonAuth = () => {
		if (state.isAuth) {
			return false;
		}
		setShowAuthModal(true);
		addToLocalStorage("barbie");
		return true;
	};

	const gaUploadImage = () => {
		ga.event({
			action: `BarbieSelfieGenerator page: upload image`,
			params: {
				event_category: "BarbieSelfieGenerator page",
			},
		});
	};

	const uploadFile = async (files) => {
		setUploadFileLoading(true);

		if (state.isAuth) {
			if (state?.accountData?.plan === "free" && state?.accountData?.total_credits < 1) {
				dispatch({
					type: "setVideoError",
					error_msg: "free_limit_img",
					error_code: "free_limit_img",
				});
				setUploadFileLoading(false);

				return false;
			}

			if ((state?.accountData?.plan === "pro" || state?.accountData?.plan === "promo") && state?.accountData?.total_credits < 1) {
				dispatch({
					type: "setVideoError",
					error_msg: "pro_limit",
					error_code: "pro_limit",
				});
				setUploadFileLoading(false);

				return false;
			}
		}

		gaUploadImage();
		let image = files[0];

		let fileExtension = image.name.split(".").pop();
		const ExtensionList = new Set(fileExtensionList);

		const errorMsg = dictionary["Extension .XX is not allowed. Only .mp4, .mov, .ts, .avi, .mkv, .webm, .mpg, .jpg, .png are allowed"]
			.replace("XX", fileExtension)
			.replace(" .mp4, .mov, .mkv, .webm,", "");

		if (fileExtension.toLowerCase() === "heif" || fileExtension.toLowerCase() === "heic") {
			const heic2any = (await import("heic2any")).default;

			fetch(URL.createObjectURL(image))
				.then((res) => {
					return res.blob();
				})
				.then((blob) => {
					return heic2any({ blob });
				})
				.then((conversionResult) => {
					const url = URL.createObjectURL(conversionResult);

					dispatch({
						type: "setStyleTransImg",
						image: url,
					});
					setUploadFileLoading(false);
				})
				.catch((e) => {
					dispatch({
						type: "setVideoError",
						error_msg: errorMsg,
						error_code: "extension",
					});
					setUploadFileLoading(false);
				});
			return;
		}

		if (!ExtensionList.has(fileExtension.toLowerCase())) {
			dispatch({
				type: "setVideoError",
				error_msg: errorMsg,
				error_code: "extension",
			});
			setUploadFileLoading(false);

			return false;
		}
		dispatch({
			type: "setStyleTransTask",
			task: null,
		});
		dispatch({
			type: "setStyleTransResults",
			results: [],
		});
		dispatch({
			type: "setStyleTransImg",
			image: URL.createObjectURL(image),
		});
		router.push(`/photo-into-painting?style=barbie&type=portrait`);
		setUploadFileLoading(false);
	};
	const dictionary = {
		...getCommonLocales(router.locale),
		...getErrorLocales(router.locale),
		...getRemoveBgPageLocales(router.locale),
	};

	const landingLayoutData = {
		mainStyle: "barbie",
		title: dictionary["page_berbie_portrait_title"] || "Barbie Selfie Generator: Create your own portrait",
		description:
			dictionary["page_barbie_portrait_description"] ||
			"Welcome to the world of doll-like transformations. Our AI provides you with the opportunity to turn regular photos into stunning works of art in the Barbie style.",
		main: {
			title: dictionary["page_barbie_selfie_header"] || "Barbie Selfie Generator",
			description: dictionary["page_barbie_selfie_descriptor"] || "Create your own Barbie-style portrait based on your photo using AI. Welcome to the world of doll-like transformations!",
			mainPicture: "/lending/typeA/barbiePortrait/main.jpg",
		},
		left: {
			imageSrc: "/lending/typeA/barbiePortrait/left.jpg",
			title: dictionary["page_barbie_selfie_header1"] || "Photo to Barbie Style filter",
			description:
				dictionary["page_barbie_selfie_descriptor1"] ||
				"Tired of ordinary photos and want to add magic and unique charm to them? Then you've found the perfect solution! Our AI provides you with the opportunity to turn regular photos into stunning works of art in the Barbie style.",
		},
		right: {
			imageSrc: "/lending/typeA/barbiePortrait/right.jpg",
			title: dictionary["page_barbie_header2"] || "Create a Barbie-Style Portraits using AI",
			description:
				dictionary["page_barbie_selfie_descriptor2"] ||
				"Turn ordinary photos into portraits inspired by the iconic Barbie doll has become possible thanks to artificial intelligence. Our service offers a neural network capable of automatically converting images while preserving their individual features and giving them that magical sparkle and charm of Barbie.",
		},
		faqName: "page_barbie_selfie",
	};

	let ogTitle = landingLayoutData.title;
	let ogDescription = landingLayoutData.description;

	const getCanonicalLink = (locale) => {
		const enList = ["en", "ja", "ar"];

		if (enList.includes(locale)) {
			return `https://bgrem.ai/barbie-selfie-generator/`;
		} else {
			return `https://bgrem.ai/${locale}/barbie-selfie-generator/`;
		}
	};

	return (
		<Layout mainClassName={`remove_bg magic_bg avatar-layout ${landingLayoutData.mainStyle}`} themeClassName={"barbie"}>
			<Head>
				<title>{landingLayoutData.title}</title>
				<meta name="description" content={landingLayoutData.description} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={ogTitle} />
				<meta property="og:description" content={ogDescription} />
				<meta property="og:url" content={getCanonicalLink(router.locale)} />
				<meta property="og:image" content={landingLayoutData.main.mainPicture} />
				<meta property="og:site_name" content="bgrem" />
				<meta property="og:locale" content="en_US" />
				<meta property="og:section" content="Turn picture into painting with AI" />
				<meta name="twitter:title" content={ogTitle} />
				<meta name="twitter:description" content={ogDescription} />
				<meta name="twitter:image" content={landingLayoutData.main.mainPicture} />
				<meta property="twitter:card" content="summary_large_image" />
				<meta property="twitter:image:src" content={landingLayoutData.main.mainPicture} />
				<link rel="canonical" href={getCanonicalLink(router.locale)} />
			</Head>
			<section className={`section magicBg ${style.magic_bg}`}>
				<LandingLayoutTypeA dictionary={dictionary} isLoading={uploadFileLoading} uploadFile={uploadFile} checkNonAuth={checkNonAuth} landingData={landingLayoutData} />
			</section>
			{showAuthModal && <AuthModal closeHandler={() => setShowAuthModal(false)} serviceName="PhotoToSketch" />}
		</Layout>
	);
}
